import { useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { TopNav } from '../nav/TopNav.js';
import { Sidebar } from './Sidebar.js';
import { BottomNav } from './BottomNav.js';
import { GlobalSearchBar } from '../nav/GlobalSearchBar.js';
import { cn } from '../../lib/cn.js';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const router = useRouterState();
  const isLandlordsPage = router.location.pathname === '/landlords';

  return (
    <div className="min-h-screen bg-hover-bg">
      {/* Top nav — pill tabs on desktop, hamburger on mobile */}
      <TopNav
        onMenuToggle={() => setMobileMenuOpen((v) => !v)}
        menuOpen={mobileMenuOpen}
      />

      {/* Mobile slide-over sidebar */}
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-20 bg-black/40 sm:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed top-[60px] left-0 bottom-0 z-20 w-64 bg-white border-r border-border sm:hidden overflow-y-auto">
            <Sidebar mobile onClose={() => setMobileMenuOpen(false)} />
          </div>
        </>
      )}

      {/* Main content — full width on desktop (no sidebar), padded for top nav */}
      <main
        className={cn(
          'pt-[60px] pb-20 sm:pb-6',
          'min-h-screen'
        )}
      >
        {/* Desktop: content in a framed white card container */}
        <div className="hidden sm:block px-3 pt-2 pb-3 min-h-[calc(100vh-60px)]">
          <div className="bg-white rounded-xl border border-border shadow-card min-h-full overflow-hidden flex flex-col">
            {/* Global search bar — desktop only, hidden on landlords page (has its own) */}
            {!isLandlordsPage && <GlobalSearchBar />}
            {/* Page content */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {children}
            </div>
          </div>
        </div>
        {/* Mobile: content without card wrapper */}
        <div className="sm:hidden">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <BottomNav />
    </div>
  );
}
